import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CardActions,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useApiGet, useApiSend } from '../../utils/httpClient';
import {
  getTLCDetail,
  patchTLC,
  patchTLCMember,
  postTLCMember,
  deleteTLCMember,
  replaceTLCMember,
} from '../../endpoints/resources';
import PositionDisplay from '../../Components/PositionDisplay';
import { Cancel, Save, Edit, Add } from '@mui/icons-material';
import { roles } from '../../utils/variables';
import UserSearchModal from '../../Components/UserSearchModal';
import DateFilter from '../../Components/DateFilter';

export default function TLCDetail() {
  const { id } = useParams();
  const [isSaving, setIsSaving] = useState(false);
  const [errorSaving, setErrorSaving] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [tlcError, setTlcError] = useState(null);
  const [name, setName] = useState('');
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [memberError, setMemberError] = useState(null);
  const [addMember, setAddMember] = useState(false);
  const [member, setMember] = useState(null);
  const [isPrimary, setIsPrimary] = useState(false);
  const [openMemberSearch, setOpenMemberSearch] = useState(false);
  const [startDate, setStartDate] = useState(new Date());

  const {
    data: tlcData,
    isLoading,
    refetch,
  } = useApiGet(
    ['tlc-detail', id],
    getTLCDetail,
    {
      enabled: true,
      retry: 1,
    },
    {
      id,
    }
  );

  const { mutate: updateTLC } = useApiSend(
    patchTLC,
    (data) => {
      setIsSaving(false);
      setIsEditing(false);
      setTlcError(null);
      refetch();
    },
    (error) => {
      setTlcError(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: updateTLCMember } = useApiSend(
    patchTLCMember,
    (data) => {
      setIsSaving(false);
      setMemberError(null);
      refetch();
    },
    (error) => {
      setMemberError(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: addTLCMember } = useApiSend(
    postTLCMember,
    (data) => {
      setIsSaving(false);
      setMemberError(null);
      setAddMember(false);
      setMember(null);
      setIsPrimary(false);
      setStartDate(new Date());
      refetch();
    },
    (error) => {
      setMemberError(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: removeTLCMember } = useApiSend(
    deleteTLCMember,
    (data) => {
      setIsSaving(false);
      setMemberError(null);
      refetch();
    },
    (error) => {
      setMemberError(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: replaceMember } = useApiSend(
    replaceTLCMember,
    (data) => {
      setIsSaving(false);
      setMemberError(null);
      refetch();
    },
    (error) => {
      setMemberError(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const handleEdit = () => {
    setIsEditing(true);
    setName(tlcData.name);
    setStatus(tlcData.is_active);
    setNotes(tlcData.notes);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setName(tlcData.name);
    setStatus(tlcData.is_active);
    setNotes(tlcData.notes);
  };

  const handleSave = () => {
    setIsSaving(true);
    setErrorSaving(null);
    updateTLC({
      formId: id,
      name: name,
      is_active: status,
      notes: notes,
    });
  };

  const canEdit = () => {
    let userRole = localStorage.getItem('role');
    if ([roles.Admin, roles.Support].includes(userRole)) {
      return true;
    } else {
      return false;
    }
  };

  const onSavePosition = (position) => {
    setIsSaving(true);
    setErrorSaving(null);
    if (position.action === 'newAppointment') {
      //Handle post
      if (!position.id) {
        //Handle new appointment
        addTLCMember({
          formId: id,
          user_id: position.userId,
          start_date: position.startDate,
          is_primary: position.positionId === 'Primary' ? true : false,
        });
      } else {
        replaceMember({
          formId: id,
          id: position.id,
          new_user_id: position.userId,
          start_date: position.startDate,
        });
      }
    } else if (position.action === 'editDates') {
      //Handle patch
      updateTLCMember({
        formId: id,
        id: position.id,
        start_date: position.startDate,
        end_date: position.endDate,
      });
    } else if (position.action === 'removeRecord') {
      //Handle delete
      removeTLCMember({
        formId: id,
        id: position.id,
      });
    }
  };

  const handleAddMember = () => {
    setAddMember(true);
    setOpenMemberSearch(true);
  };

  const handleSetMember = (member) => {
    setMember(member);
    setOpenMemberSearch(false);
  };

  const handleCancelAddMember = () => {
    setAddMember(false);
    setMember(null);
    setIsPrimary(false);
    setStartDate(new Date());
  };

  const handleSaveAddMember = () => {
    if (member) {
      setIsSaving(true);
      addTLCMember({
        formId: id,
        user_id: member.id,
        is_primary: isPrimary,
        start_date: startDate.toISOString(),
      });
    }
  };

  if (isLoading) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">Loading...</Typography>
      </Grid>
    );
  }
  if (!tlcData) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">No data available</Typography>
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid item xs={12} padding paddingTop={5}>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader
            title={tlcData.name}
            subheader={tlcData.is_active ? 'Active' : 'Inactive'}
            sx={{ p: 1 }}
          />
          <CardContent sx={{ p: 1 }}>
            <Grid container>
              {isEditing ? (
                <>
                  <Grid item xs={12} md={6} padding>
                    <TextField
                      label="Name"
                      value={name}
                      fullWidth={true}
                      size="small"
                      onChange={(e) => setName(e.target.value)}
                      sx={{ textAlign: 'left' }}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} md={6} padding>
                    <Typography variant="body1">
                      <b>{'Name:'}</b> {tlcData.name}
                    </Typography>
                  </Grid>
                </>
              )}
              <Grid item xs={12} md={6} padding>
                <Typography variant="body1">
                  <b>{'State:'}</b> {tlcData.state}
                </Typography>
              </Grid>
              {isEditing ? (
                <>
                  <Grid item xs={12} md={6} padding>
                    <FormControl size={'small'} fullWidth={true}>
                      <InputLabel id="state-label">Status</InputLabel>
                      <Select
                        label="Status"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                      >
                        <MenuItem value={true}>Active</MenuItem>
                        <MenuItem value={false}>Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} md={6} padding>
                    <Typography variant="body1">
                      <b>{'Status:'}</b>{' '}
                      {tlcData.is_active ? 'Active' : 'Inactive'}
                    </Typography>
                  </Grid>
                </>
              )}
              {isEditing ? (
                <>
                  <Grid item xs={12} md={6} padding>
                    <TextField
                      label="Notes"
                      value={notes}
                      fullWidth={true}
                      size="small"
                      multiline={true}
                      rows={4}
                      onChange={(e) => setNotes(e.target.value)}
                      sx={{ textAlign: 'left' }}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} padding>
                    <Typography variant="body1">
                      <b>{'Notes:'}</b> {tlcData.notes}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
          {canEdit() && (
            <CardActions>
              <Grid container>
                {isEditing ? (
                  <>
                    <Grid item xs={12} padding textAlign={'right'}>
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={handleCancel}
                        sx={{ fontWeight: 'bold' }}
                        size="small"
                      >
                        <Cancel sx={{ fontSize: 17, pr: 1 }} />
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        color="green"
                        onClick={handleSave}
                        sx={{ fontWeight: 'bold' }}
                        size="small"
                      >
                        <Save sx={{ fontSize: 17, pr: 1 }} />
                        Save
                      </Button>
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} padding textAlign={'right'}>
                      <Button
                        variant="contained"
                        color="blue"
                        onClick={handleEdit}
                        sx={{ fontWeight: 'bold' }}
                        size="small"
                      >
                        <Edit sx={{ fontSize: 17, pr: 1 }} />
                        Edit
                      </Button>
                    </Grid>
                  </>
                )}
                {tlcError && (
                  <Grid item xs={12} padding textAlign={'center'}>
                    <Typography variant="body1" color="red">
                      {tlcError}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardActions>
          )}
        </Card>
      </Grid>
      <Grid item xs={12} lg={6} padding>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title={'Contacts'} sx={{ p: 1 }} />
          <CardContent sx={{ p: 1 }}>
            {tlcData.members.map((contact) => (
              <PositionDisplay
                id={contact.id}
                position={contact.is_primary ? 'Primary' : 'Contact'}
                positionId={contact.is_primary ? 'Primary' : 'Contact'}
                committeeId={null}
                name={contact.name}
                startDate={contact.start_date}
                endDate={contact.end_date}
                canEdit={canEdit()}
                isSaving={isSaving}
                errorSavingText={errorSaving}
                onSave={onSavePosition}
                userId={contact.user_id}
                pageFrom={'tlc'}
                key={contact.id}
              />
            ))}
            {tlcData.members.length === 0 && (
              <Grid item xs={12} textAlign={'center'} paddingTop={2}>
                <Typography variant="body1">No contacts assigned</Typography>
              </Grid>
            )}
            {memberError && (
              <Grid item xs={12} padding textAlign={'center'}>
                <Typography variant="body1" color="red">
                  {memberError}
                </Typography>
              </Grid>
            )}
            {addMember && (
              <>
                <Grid item xs={12} padding>
                  <Divider />
                </Grid>
                <Grid item xs={12} padding textAlign={'center'}>
                  <Typography variant="body1">New Contact</Typography>
                </Grid>
                <Grid item xs={12} paddingLeft>
                  <Typography variant="body1">
                    <b>{'Name:'}</b>{' '}
                    {member ? (
                      `${member.first_name} ${member.last_name}`
                    ) : (
                      <Button
                        variant="contained"
                        color="green"
                        onClick={() => setOpenMemberSearch(true)}
                        sx={{ fontWeight: 'bold' }}
                        size="small"
                      >
                        <Add sx={{ fontSize: 17, pr: 1 }} />
                        Search for Member
                      </Button>
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12} paddingLeft>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isPrimary}
                        onChange={(e) => setIsPrimary(e.target.checked)}
                      />
                    }
                    label="Primary"
                  />
                </Grid>
                <Grid item xs={12} paddingLeft paddingTop>
                  <DateFilter
                    label="Start Date"
                    filterValue={startDate}
                    updateFilters={setStartDate}
                  />
                </Grid>
              </>
            )}
          </CardContent>
          {canEdit() && (
            <CardActions>
              <Grid container>
                {addMember ? (
                  <>
                    <Grid item xs={12} padding textAlign={'center'}>
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={handleCancelAddMember}
                        sx={{ fontWeight: 'bold', mr: 1 }}
                        size="small"
                      >
                        <Cancel sx={{ fontSize: 17, pr: 1 }} />
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        color="green"
                        onClick={handleSaveAddMember}
                        sx={{ fontWeight: 'bold' }}
                        size="small"
                        disabled={!member || !startDate || isSaving}
                      >
                        <Save sx={{ fontSize: 17, pr: 1 }} />
                        Save Contact
                      </Button>
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} padding textAlign={'center'}>
                      <Button
                        variant="contained"
                        color="green"
                        onClick={handleAddMember}
                        sx={{ fontWeight: 'bold' }}
                        size="small"
                      >
                        <Add sx={{ fontSize: 17, pr: 1 }} />
                        Add Contact
                      </Button>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardActions>
          )}
        </Card>
      </Grid>
      <Grid item xs={12} md={6} padding>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title={'Local Chapters'} sx={{ p: 1 }} />
          <CardContent sx={{ p: 1 }}>
            <Grid container>
              {tlcData.chapters.map((localChapter) => (
                <Grid item xs={12} padding key={localChapter.chapter_id}>
                  <Typography variant="body1">
                    <b>{'Name:'}</b> {localChapter.chapter_name}
                  </Typography>
                </Grid>
              ))}
              {tlcData.chapters.length === 0 && (
                <Grid item xs={12} textAlign={'center'} paddingTop={2}>
                  <Typography variant="body1">
                    No local chapters assigned
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      <UserSearchModal
        modalOpen={openMemberSearch}
        setUser={handleSetMember}
        setModalOpen={setOpenMemberSearch}
        chapter={null}
        spd={null}
        activeMembers={true}
        userObjectRet={true}
        allowSetToMe={true}
      />
    </Grid>
  );
}
